import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';

import { SpotlightTransition } from '@atlaskit/onboarding';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isUnauthenticatedError } from '@confluence/error-boundary/entry-points/isUnauthenticatedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { WithModernizeChangeboardingActive } from '@confluence/onboarding-helpers/entry-points/hooks/useModernizeChangeboardingActive.ts';

import { useCombinedSegments } from './hooks/useCombinedSegments.tsx';
import { useExcludeFromModernizeGAChangeboarding } from './hooks/useExcludeFromModernizeGAChangeboarding.ts';

type State = {
	stepIndex: number;
	isDone: boolean;
};

type Action = { type: 'NEXT' | 'BACK' | 'COMPLETE' | 'RESET' };

const initialState: State = {
	stepIndex: 0,
	isDone: false,
};

function reducer(state: State, action: Action) {
	switch (action.type) {
		case 'NEXT':
			return { ...state, stepIndex: state.stepIndex + 1 };
		case 'BACK':
			return { ...state, stepIndex: state.stepIndex - 1 };
		case 'COMPLETE':
			return { ...state, isDone: true };
		case 'RESET':
			return { ...state, ...initialState };
		default:
			return state;
	}
}

function isExpectedError(error: Error) {
	return isUnauthenticatedError(error) || isUnauthorizedError(error);
}

const ModernizeChangeboardingInner = () => {
	const transitionId = useTransitionId();
	const { modal, steps, loading, error, onComplete } = useCombinedSegments();
	const [{ stepIndex, isDone }, dispatch] = useReducer(reducer, initialState);
	const [messageType, setMessageType] = useState<'transactional' | 'engagement' | 'marketing'>(
		'engagement',
	);
	const isLast = stepIndex === steps.length - 1;
	const isFirst = stepIndex === 0;

	const onNext = useCallback(() => {
		if (isLast) {
			onComplete();
			dispatch({ type: 'COMPLETE' });
			return;
		}

		setMessageType('transactional');
		dispatch({ type: 'NEXT' });
	}, [isLast, onComplete, dispatch]);

	const onBack = useCallback(() => {
		if (isFirst) {
			return;
		}

		dispatch({ type: 'BACK' });
	}, [isFirst, dispatch]);

	const onDismiss = useCallback(() => {
		onComplete();
		dispatch({ type: 'COMPLETE' });
	}, [onComplete, dispatch]);

	const TourStep = steps[stepIndex];

	useEffect(() => {
		// If a url change happens after the current tour is completed, then resetting it.
		// This is to ensure eligible tour for current url can be shown again, even though a different tour was completed at previous url.
		// Note: transitionId is 0 when it's the initial load. We don't want to reset the tour on initial load.
		if (transitionId > 0) {
			dispatch({ type: 'RESET' });
		}
	}, [dispatch, transitionId]);

	if (error) {
		if (isExpectedError(error)) {
			markErrorAsHandled(error);
			return null;
		}

		return <ErrorDisplay error={error} />;
	}

	if (loading || isDone) {
		return null;
	}

	if (modal) {
		return <WithModernizeChangeboardingActive>{modal}</WithModernizeChangeboardingActive>;
	}

	if (!TourStep) {
		return null;
	}

	// if there is a modal eligible to be shown, show it instead of the tour steps
	return (
		<WithModernizeChangeboardingActive>
			<SpotlightTransition>
				<TourStep
					onNext={onNext}
					onBack={onBack}
					onDismiss={onDismiss}
					progress={{
						step: stepIndex + 1,
						steps: steps.length,
						isFirst,
						isLast,
					}}
					messageType={messageType}
				/>
			</SpotlightTransition>
		</WithModernizeChangeboardingActive>
	);
};

export const ModernizeChangeboarding = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(() => {
	const { isNewUser } = useContext(SPAViewContext);
	const { isLicensed, isGuest } = useAccessStatus();

	// If the user is neither licensed nor a guest, we don't show the changeboarding. This is because:
	// * Changeboarding is not applicable for anonymous users, since they can all be considered new.
	// * Onboarding state, which we use to track completion, is only available for licensed and guest users.
	if (!isLicensed && !isGuest) {
		return null;
	}

	// If the user is new and seeing ModernizeChangeboarding, then they joined after GA.
	// In that case we don't want to show them changeboarding, so we immediately dismiss it.
	if (isNewUser) {
		return <ExcludeFromModernizeGAChangeboarding />;
	}

	return <ModernizeChangeboardingInner />;
});

const ExcludeFromModernizeGAChangeboarding = () => {
	useExcludeFromModernizeGAChangeboarding();

	return null;
};
